/**
 * Date-only arithmetic. All dates are ISO strings "YYYY-MM-DD" and are converted to
 * integer "day numbers" (days since 1970-01-01 UTC) for computation so the engine has no
 * timezone or DST behaviour.
 */

export type ISODate = string;
export type DayNumber = number;

const MS_PER_DAY = 86_400_000;

export function isISODate(value: unknown): value is ISODate {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

export function toDay(iso: ISODate): DayNumber {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  const d = Number(iso.slice(8, 10));
  return Math.floor(Date.UTC(y, m - 1, d) / MS_PER_DAY);
}

export function fromDay(day: DayNumber): ISODate {
  const dt = new Date(day * MS_PER_DAY);
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** 0 = Sunday … 6 = Saturday */
export function weekdayOfDay(day: DayNumber): number {
  // 1970-01-01 was a Thursday (4)
  return (((day + 4) % 7) + 7) % 7;
}

export function addDays(iso: ISODate, n: number): ISODate {
  return fromDay(toDay(iso) + n);
}

export function diffDays(a: ISODate, b: ISODate): number {
  return toDay(b) - toDay(a);
}

export function compareISO(a: ISODate, b: ISODate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function maxISO(...dates: ISODate[]): ISODate {
  return dates.reduce((m, d) => (d > m ? d : m));
}

export function minISO(...dates: ISODate[]): ISODate {
  return dates.reduce((m, d) => (d < m ? d : m));
}

export function todayISO(): ISODate {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function startOfMonthDay(day: DayNumber): DayNumber {
  const dt = new Date(day * MS_PER_DAY);
  return Math.floor(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), 1) / MS_PER_DAY);
}

export function addMonthsDay(day: DayNumber, n: number): DayNumber {
  const dt = new Date(day * MS_PER_DAY);
  return Math.floor(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth() + n, dt.getUTCDate()) / MS_PER_DAY);
}

export function dayOfMonth(day: DayNumber): number {
  return new Date(day * MS_PER_DAY).getUTCDate();
}

/* Display strings live in @/lib/date-format. The engine only does arithmetic. */
